import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CookingPot, Plus, Check, MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = ['Todos', 'Super Barriguinhas', 'Super Mamãs', 'Super Pré Mamãs', 'Super Bebés', 'Geral'];

export default function CommunityRecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  const [form, setForm] = useState({ titulo: '', ingredientes: '', modo_preparacao: '', programa: 'Todos', mostrar_nome_autora: false });

  const fetch = useCallback(async () => {
    try {
      const params = filter ? `?program=${filter}` : '';
      const { data } = await axios.get(`${API}/api/community-recipes${params}`);
      setRecipes(data);
    } catch {}
  }, [filter]);
  useEffect(() => { fetch(); }, [fetch]);

  const submit = async () => {
    if (!form.titulo.trim() || !form.ingredientes.trim()) { toast.error('Título e ingredientes são obrigatórios'); return; }
    try {
      await axios.post(`${API}/api/community-recipes/submit`, form);
      toast.success('Receita enviada para aprovação!');
      setShowSubmit(false);
      setForm({ titulo: '', ingredientes: '', modo_preparacao: '', programa: 'Todos', mostrar_nome_autora: false });
    } catch { toast.error('Erro ao submeter'); }
  };

  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filtered = search ? recipes.filter(r => norm(r.titulo).includes(norm(search)) || norm(r.ingredientes).includes(norm(search))) : recipes;

  return (
    <div className="fade-in" data-testid="community-recipes-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Receitas da Comunidade</h2>
          <p className="text-xs text-brand-muted mt-0.5">Partilhadas e aprovadas pela nutricionista</p>
        </div>
        <button onClick={() => setShowSubmit(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="submit-recipe-btn">
          <Plus size={16} /> Partilhar receita
        </button>
      </div>

      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar receitas..." className="pl-9 rounded-xl border-brand-border" />
      </div>

      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${!filter ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Todas</button>
        {PROGRAMS.map(p => (
          <button key={p} onClick={() => setFilter(filter === p ? '' : p)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${filter === p ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{p}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12"><CookingPot size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" /><p className="text-brand-muted text-sm">Sem receitas publicadas.</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`recipe-${r.id}`}>
              <h3 className="text-sm font-medium text-brand-text">{r.titulo}</h3>
              <div className="flex items-center gap-2 mt-1 mb-2">
                <span className="text-[9px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{r.programa}</span>
                {r.autora_nome && <span className="text-[9px] text-brand-muted">por {r.autora_nome}</span>}
              </div>
              <div className="bg-brand-bg/30 rounded-xl p-3 mb-2">
                <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">Ingredientes</p>
                <Md as="p" className="text-xs text-brand-text whitespace-pre-line">{r.ingredientes}</Md>
              </div>
              {r.modo_preparacao && (
                <div>
                  <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">Modo de preparação</p>
                  <Md as="p" className="text-xs text-brand-text whitespace-pre-line">{r.modo_preparacao}</Md>
                </div>
              )}
              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-brand-border/30">
                <Check size={12} className="text-brand-green" />
                <span className="text-[9px] text-brand-green">Aprovado por {r.aprovada_por || 'nutricionista'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Partilhar receita com a comunidade</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Título *</Label><Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Nome da receita" className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Ingredientes *</Label><Textarea value={form.ingredientes} onChange={e => setForm({ ...form, ingredientes: e.target.value })} placeholder="Um ingrediente por linha" className="rounded-xl border-brand-border" rows={4} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Modo de preparação</Label><Textarea value={form.modo_preparacao} onChange={e => setForm({ ...form, modo_preparacao: e.target.value })} placeholder="Descreve os passos..." className="rounded-xl border-brand-border" rows={4} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Programa</Label>
              <div className="flex gap-1.5 flex-wrap">
                {PROGRAMS.map(p => (<button key={p} type="button" onClick={() => setForm({ ...form, programa: p })} className={`px-3 py-1.5 rounded-full text-[11px] font-medium ${form.programa === p ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{p}</button>))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.mostrar_nome_autora} onChange={e => setForm({ ...form, mostrar_nome_autora: e.target.checked })} className="rounded border-brand-border w-4 h-4" />
              <span className="text-xs text-brand-text">Mostrar o meu nome na receita</span>
            </label>
            <p className="text-[10px] text-brand-muted">A tua receita será publicada após aprovação pela nutricionista.</p>
            <button onClick={submit} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Enviar para aprovação</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
